import { isStageEnvironment } from '@/core/constants';

type ShopifyConsent = {
  analytics?: boolean;
  marketing?: boolean;
  preferences?: boolean;
};

type FidesConsent = {
  analytics?: boolean;
  marketing?: boolean;
  essential?: boolean;
  data_sales_and_sharing?: boolean;
  functional?: boolean;
};

declare global {
  interface Window {
    Shopify?: {
      customerPrivacy?: {
        setTrackingConsent: (
          consent: ShopifyConsent,
          callback: () => void,
        ) => void;
      };
    };
    Fides?: {
      consent?: {
        marketing?: boolean;
        analytics?: boolean;
        preferences?: boolean;
      };
      experience?: {
        non_applicable_privacy_notices?: string[];
      };
      initialized?: boolean;
    };
  }
}

function debounce<T extends (...args: any[]) => void>(fn: T, ms = 250) {
  let t: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function loadShopifyPrivacyApi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return resolve();
    if (window.Shopify?.customerPrivacy) return resolve();

    const s = document.createElement('script');
    s.src =
      'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/**
 * Reads current consent from window.Fides, which looks like:
 * window.Fides = { consent: { marketing: boolean }, experience: { non_applicable_privacy_notices: [...] }, ... }
 */
function readFidesConsent(): ShopifyConsent | null {
  const fidesClient = window.Fides;
  if (!fidesClient) return null;

  const consent: FidesConsent = fidesClient.consent || {};
  const nonApplicable =
    fidesClient.experience?.non_applicable_privacy_notices || [];

  // Helper function to process consent values
  const processConsent = (key: keyof FidesConsent): boolean => {
    const hasOwnConsent = fidesClient.consent
      ? Object.prototype.hasOwnProperty.call(fidesClient.consent, key)
      : false;

    if (hasOwnConsent) {
      return Boolean(consent[key]);
    } else {
      return nonApplicable.includes(key) ? true : false;
    }
  };

  if (isStageEnvironment()) {
    console.log({ consent, nonApplicable });
  }

  return {
    analytics: processConsent('analytics'),
    marketing: processConsent('marketing'),
    //  Preferences are always true
    preferences: true,
  };
}

async function setShopifyConsent(consent: ShopifyConsent) {
  await loadShopifyPrivacyApi();
  const customerPrivacy = window.Shopify?.customerPrivacy;

  const payload = {
    ...consent,
    headlessStorefront: true,
    checkoutRootDomain: `shop.${process.env.NEXT_PUBLIC_BACKEND_DOMAIN}`,
    storefrontRootDomain: process.env.NEXT_PUBLIC_BACKEND_DOMAIN,
    storefrontAccessToken: process.env.NEXT_PUBLIC_SHOPIFY_ACCESS_TOKEN,
  };

  if (customerPrivacy?.setTrackingConsent) {
    // TODO: Rudderstack consent tracking can be added here
    customerPrivacy.setTrackingConsent(payload, () => {
      if (isStageEnvironment()) {
        console.log('[consent-bridge] Shopify consent set:', consent);
      }
    });
  } else {
    console.warn('[consent-bridge] Shopify customerPrivacy not available');
  }
}

const debouncedSet = debounce(setShopifyConsent, 200);

// Fides is available in US only
export function startFidesToShopifyBridge() {
  if (typeof window === 'undefined' || process.env.shop)
    return () => {
      // No-op function for server-side rendering
    };

  const syncNow = () => {
    const mapped = readFidesConsent();
    if (mapped) debouncedSet(mapped);
  };

  window.addEventListener('FidesUpdated', syncNow);

  // Seed once (right away) if Fides is already initialized
  if (window.Fides?.initialized) {
    syncNow();
  } else {
    // If not yet initialized, try again shortly
    setTimeout(syncNow, 500);
  }

  return () => {
    window.removeEventListener('FidesUpdated', syncNow);
  };
}
